import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

type Props = {
  title: string;
  /** Optional node rendered next to the title — used for a status badge etc. */
  titleSuffix?: ReactNode;
  /** Optional action(s) rendered to the right of the title, before the Back button.
   *  Use for page-scoped actions like "Preview" or "Export". */
  actions?: ReactNode;
  /** Called when the user clicks the Back button. */
  onBack: () => void;
};

/**
 * Title-on-the-left, text-button-Back-on-the-right page header. Used at the top of
 * the Workflow Details, Approver Review, and Version History pages. Keeps the
 * spacing/layout identical between them.
 *
 * Layout lives in global.css under `.wf-page-header` rather than in a `style`
 * object here: below 768px the row becomes a vertical stack (Back, then the
 * title, then its status), and an inline declaration on the element would
 * outrank that band instead of cascading under it.
 */
export function PageHeader({ title, titleSuffix, actions, onBack }: Props) {
  const { t } = useTranslation();
  return (
    <div className="wf-page-header">
      <div className="wf-page-header__title">
        {/* Semantically the page's <h1> (WCAG 2.1 — 1.3.1, 2.4.6). Pages used to
            open at h3 with no h1 anywhere, which breaks navigate-by-heading —
            a primary screen-reader technique — and reads as a document whose
            top two levels are missing. `level={1}` with the level-3 type scale
            pinned back on, so the semantics change and the design does not. */}
        <Title level={1} style={{ margin: 0, fontSize: 24, lineHeight: 1.35, fontWeight: 600 }}>
          {title}
        </Title>
        {titleSuffix && (
          <>
            {/* Subtle en-dash separator so the suffix (status badge) reads like part of
                the title, not an afterthought floating next to it. */}
            {/* Decorative punctuation — hidden from assistive tech (it would be
                read as "en dash" between the title and its status) and darkened
                to clear 4.5:1 for the sighted reader (1.4.3). It is dropped on
                phone, where the two sit on separate lines and a dangling dash
                reads as a bullet rather than as a separator. */}
            <span aria-hidden className="wf-page-header__dash">
              –
            </span>
            {titleSuffix}
          </>
        )}
      </div>
      <div className="wf-page-header__actions">
        {actions}
        {/* Authored last — it is the right-hand end of the desktop row. The
            phone band lifts it to the top of the stack with `order`, which is
            a paint-order change only: the tab order still reaches the page
            actions before Back, as it does at every other width. */}
        <Button
          type="text"
          className="wf-page-header__back"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
        >
          {t('common.back')}
        </Button>
      </div>
    </div>
  );
}
